import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, Star, Sparkles, Calendar, MapPin, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import {
  collectionPageLD, breadcrumbLD, itemListLD,
} from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = [
  { value: '', label: 'All cities' },
  { value: 'denver', label: 'Denver' },
  { value: 'las-vegas', label: 'Las Vegas' },
  { value: 'los-angeles', label: 'Los Angeles' },
  { value: 'charlotte', label: 'Charlotte' },
];

/**
 * /organizations — Public directory of event organizers using Eventism.
 * Phase 4 Org↔Venue marketplace surface + Phase 5 SEO (CollectionPage +
 * BreadcrumbList + ItemList JSON-LD).
 */
export default function OrganizationsDirectory() {
  const [orgs, setOrgs] = useState([]);
  const [city, setCity] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (city) params.city = city;
      if (q) params.q = q;
      const res = await axios.get(`${API}/organizations/public`, { params });
      setOrgs(res.data || []);
    } catch (_) {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [city, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);   // debounce
    return () => clearTimeout(t);
  }, [load]);

  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Organizers', url: '/organizations' },
  ]);
  const collection = collectionPageLD(
    'Event Organizers on Eventism',
    'Browse independent event organizers and promoters running ticketed events on Eventism — concerts, festivals, food-and-wine events, conferences, and more.',
  );
  const items = itemListLD(
    orgs.slice(0, 30).map((o) => ({
      name: o.display_name || o.legal_name,
      url: `/organizations/${o.slug}`,
    })),
    'Event Organizers',
  );

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="organizations-directory">
      <SEO
        title="Event Organizers — Eventism"
        description="Browse independent event organizers and promoters running ticketed events on Eventism."
        canonical="/organizations"
        keywords="event organizers, promoters, ticketed events, concerts, festivals, Eventism"
        structuredData={[collection, breadcrumb, items]}
      />

      <div className="max-w-6xl mx-auto px-4 space-y-6">

        {/* Header */}
        <div className="space-y-3">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
            Marketplace · Event organizers
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]">
            Discover the people <span className="bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">throwing the best events</span>.
          </h1>
          <p className="text-muted-foreground max-w-2xl text-base">
            Independent organizers and promoters who ticket their events on Eventism.
            Same low fees as Eventbrite, better marketing tooling.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link to="/organizations/apply" data-testid="org-apply-cta">
              <Button className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white">
                Apply to be an Organizer <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by organizer name…"
                className="pl-10"
                data-testid="org-directory-search"
              />
            </div>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="h-10 rounded-md border border-border bg-card px-3 text-sm"
              data-testid="org-directory-city"
            >
              {CITIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="org-directory-count">
              {loading ? 'Loading…' : `${orgs.length} organizer${orgs.length === 1 ? '' : 's'}`}
            </span>
          </CardContent>
        </Card>

        {/* Grid */}
        {loading && orgs.length === 0 ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
          </div>
        ) : orgs.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="org-directory-empty">
            No organizers match your filters yet. Try a different city, or be the first —{' '}
            <Link to="/organizations/apply" className="text-indigo-400 underline">
              apply now
            </Link>.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {orgs.map((o) => <OrgCard key={o.organization_id} org={o} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function OrgCard({ org }) {
  return (
    <Link
      to={`/organizations/${org.slug}`}
      data-testid={`org-card-${org.slug}`}
      className="group block"
    >
      <Card className="border-border hover:border-indigo-500/40 transition-colors h-full">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 border border-indigo-500/30 flex items-center justify-center shrink-0 overflow-hidden">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.display_name || org.legal_name} className="h-full w-full object-cover" />
              ) : (
                <Sparkles className="h-5 w-5 text-indigo-300" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold truncate flex items-center gap-1.5">
                {org.display_name || org.legal_name}
                {org.is_verified && (
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                )}
              </h3>
              {org.primary_city && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {String(org.primary_city).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())}
                </p>
              )}
            </div>
          </div>
          {org.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2">{org.bio}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
            {org.avg_rating != null && (
              <span className="flex items-center gap-1 text-amber-300 font-medium">
                <Star className="h-3 w-3 fill-amber-300" />
                {Number(org.avg_rating).toFixed(1)}
                <span className="text-muted-foreground/70">({org.review_count || 0})</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {org.events_hosted_count || 0} event{org.events_hosted_count === 1 ? '' : 's'}
            </span>
            {(org.event_types || []).slice(0, 2).map((t) => (
              <Badge key={t} variant="secondary" className="bg-indigo-500/10 text-indigo-200 border-indigo-500/20 text-[10px]">{t}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
